import {textToHtml} from '@frontapp/editor-html';
import {Calendar, EventInput} from '@fullcalendar/core';
import {DateInput} from '@fullcalendar/core/datelib/env';
import classNames from 'classnames';
import _, {compact, isArray, isNumber, isString} from 'lodash';
import memoize from 'lru-memoize';
import {DateTime} from 'luxon';
import {mix} from 'polished';

import {globalDocument} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isHtmlElement} from '../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {
  build12Or24hOptionsForFormat,
  TimeFormatsEnum,
} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {filterValidDateTime} from '../../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {Localizer} from '../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../../libs/shared/core/src/helpers/internationalization/message';
import {
  CleanHtml,
  iSolemnlySwearThisHtmlIsClean,
} from '../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {ResourceMap} from '../../../../libs/shared/core/src/store/commonAppTypes';
import {PaletteColor} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {buildFullCalendarArbitraryEventId} from '../../../../srcCommon/helpers/calendarHelpers';
import {
  buildUpdatedCalendarEventVersion,
  CombinedCalendarEventParameters,
  computeCalendarStartEndDateTime,
  doesEventDataMatchParameters,
  findCalendarEventVersionColor,
  isEventEditable,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {ArbitraryCalendarEventModel} from '../../../models/api/arbitraryCalendarEventModel';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {CalendarEventModel, CalendarEventStatusesEnum} from '../../../models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionShowAsTypesEnum,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarModel, CalendarProviderTypesEnum} from '../../../models/api/calendarModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {ParticipantResponseStatusesEnum} from '../../../models/api/participantModel';
import {CalendarsViewTypesEnum} from '../../../store/panels/calendars/calendarsParameters';

/*
 * Constants.
 */

const fullCalendarAllDayTimeFormat = 'yyyy-MM-dd';
const viewTypesWithDragSupport = [CalendarsViewTypesEnum.DAY, CalendarsViewTypesEnum.WEEK];
const pixelHeightPerMinute = 0.85;

export interface FullCalendarEvent extends EventInput {
  id: string;
}

export function buildFullCalendarArbitraryEvent(
  arbitraryCalendarEvent: ArbitraryCalendarEventModel,
  theme: UserTheme,
  viewType: CalendarsViewTypesEnum,
  paletteColorOverride: PaletteColorsEnum | undefined,
  viewingNowAddressToColor: ResourceMap<PaletteColorsEnum>,
): FullCalendarEvent {
  const shouldRenderAsAllDayEvent = arbitraryCalendarEvent.isAllDay;
  const isStandardEventWithMultiDayDuration = false;
  const isStandardMonthViewEvent = viewType === CalendarsViewTypesEnum.MONTH && !shouldRenderAsAllDayEvent;

  const {startDateTime, endDateTime} = computeCalendarStartEndDateTime(arbitraryCalendarEvent);

  const isInPast = endDateTime < DateTime.local();

  // Default calendar color is blue.
  const colorName =
    paletteColorOverride ??
    viewingNowAddressToColor[arbitraryCalendarEvent.address] ??
    PaletteColorsEnum.BLUE;
  const color = theme.palette[colorName];

  return {
    id: buildFullCalendarArbitraryEventId(arbitraryCalendarEvent.id),
    summary: arbitraryCalendarEvent.summary,
    allDay: shouldRenderAsAllDayEvent,
    start: buildFullCalendarStart(shouldRenderAsAllDayEvent, startDateTime),
    end: buildFullCalendarEnd(
      shouldRenderAsAllDayEvent,
      isStandardEventWithMultiDayDuration,
      startDateTime,
      endDateTime,
    ),
    durationMinutes: shouldRenderAsAllDayEvent
      ? undefined
      : endDateTime.diff(startDateTime, 'minutes').minutes,
    editable: false,
    isFocusedEvent: false,
    isPrivateEvent: false,
    isArbitraryEvent: true,
    isTimeSlot: false,
    provider: CalendarProviderTypesEnum.GOOGLE,
    showAs: arbitraryCalendarEvent.showAs,
    ...buildArbitraryEventColorProperties(
      arbitraryCalendarEvent.showAs,
      isStandardMonthViewEvent,
      isInPast,
      theme,
      color,
    ),
  };
}

function buildArbitraryEventColorProperties(
  showAs: CalendarEventVersionShowAsTypesEnum,
  isStandardMonthViewEvent: boolean,
  isInPast: boolean,
  theme: UserTheme,
  color: PaletteColor,
) {
  const colorShade = isInPast ? memoizedMix(0.2, color.shade40, theme.greys.white) : color.shade40;
  const textColorShade = isInPast ? theme.greys.shade70 : theme.invertedText;

  const className = classNames(
    `fc-event-${
      // The styling for busy events is the same as accepted events. The styling for free events is
      // the same as pending events.
      showAs === CalendarEventVersionShowAsTypesEnum.BUSY
        ? ParticipantResponseStatusesEnum.ACCEPTED
        : ParticipantResponseStatusesEnum.PENDING
    }`,
    {
      'fc-event-with-hover': true,
      'fc-event-focused': false,
      'fc-event-popover': false,
      'fc-standard-month-view-event': isStandardMonthViewEvent,
      'fc-event-cancelled': false,
    },
  );

  if (showAs === CalendarEventVersionShowAsTypesEnum.BUSY) {
    return {
      backgroundColor: isStandardMonthViewEvent ? 'none' : colorShade,
      borderColor: colorShade,
      textColor: textColorShade,
      dotBackgroundColor: colorShade,
      dotBorderColor: colorShade,
      className,
    };
  }
  return {
    backgroundColor: isStandardMonthViewEvent ? 'none' : colorShade,
    borderColor: colorShade,
    textColor: isInPast ? theme.greys.shade60 : colorShade,
    dotBackgroundColor: theme.greys.white,
    dotBorderColor: colorShade,
    className,
  };
}

export function buildFullCalendarEvent(
  viewType: CalendarsViewTypesEnum,
  calendarEventVersion: CalendarEventVersionModel,
  calendarEvent: CalendarEventModel,
  calendarVersion: CalendarVersionModel,
  calendar: CalendarModel,
  instanceStartDate: number | undefined,
  {
    highlightedEventParameters,
    focusedCalendarEventParameters,
    popoverCalendarEventParameters,
  }: CombinedCalendarEventParameters,
  theme: UserTheme,
  colorOverride?: PaletteColorsEnum,
): FullCalendarEvent {
  const isHighlightedEvent = doesEventDataMatchParameters(
    highlightedEventParameters,
    calendarVersion,
    calendarEventVersion,
    instanceStartDate,
  );
  const isFocusedEvent = doesEventDataMatchParameters(
    focusedCalendarEventParameters,
    calendarVersion,
    calendarEventVersion,
    instanceStartDate,
  );
  const isPopoverEvent = doesEventDataMatchParameters(
    popoverCalendarEventParameters,
    calendarVersion,
    calendarEventVersion,
    instanceStartDate,
  );

  const updatedCalendarEventVersion = buildUpdatedCalendarEventVersion(
    calendarEventVersion,
    focusedCalendarEventParameters,
    isFocusedEvent,
  );

  const hasStartEndOverride = Boolean(
    isFocusedEvent &&
      focusedCalendarEventParameters &&
      focusedCalendarEventParameters.overrides &&
      focusedCalendarEventParameters.overrides.startEnd,
  );
  const instanceStartDateTime =
    instanceStartDate && !hasStartEndOverride ? DateTime.fromMillis(instanceStartDate) : undefined;
  const {startDateTime, endDateTime} = computeCalendarStartEndDateTime(
    updatedCalendarEventVersion,
    filterValidDateTime(instanceStartDateTime),
  );

  // isStandardEventWithMultiDayDuration is an event that is not 'All-Day' but lasts longer than 24h
  const isStandardEventWithMultiDayDuration =
    !updatedCalendarEventVersion.isAllDay && endDateTime.diff(startDateTime, 'hours').hours > 24;

  // If a standard event spans multiple days, we actually render it in the all-day section.
  const shouldRenderAsAllDayEvent =
    updatedCalendarEventVersion.isAllDay || isStandardEventWithMultiDayDuration;

  const isInPast = endDateTime < DateTime.local();

  return {
    id: buildFullCalendarEventId(calendarEventVersion.uid, calendarVersion.id, instanceStartDate),
    calendarEventVersionUid: calendarEventVersion.uid,
    calendarVersionId: calendarVersion.id,
    calendarVersionAccessRole: calendarVersion.accessRole,
    instanceStartDate,
    summary: updatedCalendarEventVersion.summary,
    allDay: shouldRenderAsAllDayEvent,
    start: buildFullCalendarStart(shouldRenderAsAllDayEvent, startDateTime),
    end: buildFullCalendarEnd(
      shouldRenderAsAllDayEvent,
      isStandardEventWithMultiDayDuration,
      startDateTime,
      endDateTime,
    ),
    durationMinutes: shouldRenderAsAllDayEvent
      ? undefined
      : endDateTime.diff(startDateTime, 'minutes').minutes,
    editable:
      viewTypesWithDragSupport.includes(viewType) &&
      isEventEditable(calendar, calendarVersion, calendarEventVersion) &&
      calendarEventVersion.isOrganizerCopy,
    isFocusedEvent,
    isPrivateEvent: calendarEventVersion.visibility === CalendarEventVersionVisibilityTypesEnum.PRIVATE,
    isTimeSlot: false,
    provider: calendar.provider,
    ...buildEventColorProperties(
      viewType,
      updatedCalendarEventVersion,
      calendarVersion,
      shouldRenderAsAllDayEvent,
      isInPast,
      isHighlightedEvent || isFocusedEvent,
      isPopoverEvent,
      calendarEvent.status === CalendarEventStatusesEnum.CANCELLED,
      theme,
      colorOverride,
    ),
  };
}

export function buildFullCalendarSlot(
  slotId: number,
  startDateTime: DateTime<true>,
  endDateTime: DateTime<true>,
  theme: UserTheme,
): FullCalendarEvent {
  const isInPast = endDateTime < DateTime.local();

  const className = classNames('fc-scheduling-slot', 'fc-event-with-hover', {
    'fc-scheduling-slot-past': isInPast,
  });

  return {
    id: buildFullCalendarSlotId(slotId),
    slotId,
    allDay: false,
    start: startDateTime.toISO(),
    end: endDateTime.toISO(),
    editable: true,
    isTimeSlot: true,

    backgroundColor: isInPast ? theme.palette.green.shade30 : theme.palette.green.shade40,
    borderColor: isInPast ? theme.palette.green.shade30 : theme.palette.green.shade40,
    textColor: theme.greys.shade80,
    dotBackgroundColor: theme.palette.green.shade10,
    dotBorderColor: theme.palette.green.shade10,
    className,
  };
}

function buildFullCalendarStart(shouldRenderAsAllDayEvent: boolean, startDateTime: DateTime<true>) {
  if (shouldRenderAsAllDayEvent) {
    return startDateTime.toFormat(fullCalendarAllDayTimeFormat);
  }

  return startDateTime.toISO();
}

function buildFullCalendarEnd(
  shouldRenderAsAllDayEvent: boolean,
  isStandardEventWithMultiDayDuration: boolean,
  startDateTime: DateTime<true>,
  endDateTime: DateTime<true>,
) {
  if (shouldRenderAsAllDayEvent) {
    return (
      endDateTime
        // Only add an extra day if it's not a real all day event lasting >24h
        .plus(isStandardEventWithMultiDayDuration ? {days: 1} : {})
        .toFormat(fullCalendarAllDayTimeFormat)
    );
  }

  // For standard events that have duration of less than 15 minutes, we artificially display as 15 minute events
  // so that their meeting topic is visible.
  if (endDateTime.diff(startDateTime, 'minutes').minutes < 15) {
    return startDateTime.plus({minutes: 15}).toISO();
  }

  return endDateTime.toISO();
}

// Combine the calendarEventVersion UID, calendarVersion ID, and
// the instance startDateTime (if this is a recurring instance event)
// to create a unique identifier for an event rendered within FullCalendar.
export const fullCalendarEventIdPrefix = 'fullcalendar_event';
export const buildFullCalendarEventId = (
  calendarEventVersionUid: string,
  calendarVersionId: number,
  instanceDateTime: number | undefined,
  isMoreEventsPopoverEvent?: boolean,
) =>
  `${fullCalendarEventIdPrefix}-${compact([
    calendarVersionId,
    calendarEventVersionUid,
    instanceDateTime,
  ]).join('-')}${isMoreEventsPopoverEvent ? '-more_popover' : ''}`;

export const buildFullCalendarSlotId = (slotId: number) => `fullcalendar_schedulingslot-${slotId}`;

function buildEventColorProperties(
  viewType: CalendarsViewTypesEnum,
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
  isAllDay: boolean,
  isInPast: boolean,
  isFocused: boolean,
  isPopoverEvent: boolean,
  isCancelled: boolean,
  theme: UserTheme,
  colorOverride?: PaletteColorsEnum,
) {
  const colorName = findColorName(calendarVersion, calendarEventVersion, colorOverride);
  const color = theme.palette[colorName];
  const colorShade =
    isInPast && !isFocused ? memoizedMix(0.2, color.shade40, theme.greys.white) : color.shade40;
  const textColorShade = isInPast && !isFocused ? theme.greys.shade70 : theme.invertedText;
  const responseStatus = calendarEventVersion.response && calendarEventVersion.response.status;

  const isStandardMonthViewEvent = viewType === CalendarsViewTypesEnum.MONTH && !isAllDay;
  const supportsHover = viewType !== CalendarsViewTypesEnum.SCHEDULING;

  const className = classNames(`fc-event-${responseStatus}`, {
    'fc-event-with-hover': supportsHover,
    'fc-event-focused': isFocused,
    'fc-event-popover': isPopoverEvent,
    'fc-standard-month-view-event': isStandardMonthViewEvent,
    'fc-event-cancelled': isCancelled,
  });

  switch (responseStatus) {
    case ParticipantResponseStatusesEnum.PENDING:
      return {
        backgroundColor: isStandardMonthViewEvent ? 'none' : colorShade,
        borderColor: colorShade,
        textColor: isInPast && !isFocused ? theme.greys.shade60 : colorShade,
        dotBackgroundColor: theme.greys.white,
        dotBorderColor: colorShade,
        className,
      };
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return {
        backgroundColor: isStandardMonthViewEvent ? 'none' : colorShade,
        borderColor: colorShade,
        textColor: textColorShade,
        dotBackgroundColor: colorShade,
        dotBorderColor: colorShade,
        className,
      };
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return {
        backgroundColor: isStandardMonthViewEvent ? 'none' : theme.foreground,
        borderColor: colorShade,
        textColor: textColorShade,
        gradientColor: colorShade,
        dotBackgroundColor:
          isInPast && !isFocused ? memoizedMix(0.2, color.shade30, theme.greys.white) : color.shade30,
        dotBorderColor: colorShade,
        className,
      };
    case ParticipantResponseStatusesEnum.DECLINED:
      return {
        backgroundColor: isStandardMonthViewEvent ? 'none' : colorShade,
        borderColor: colorShade,
        textColor: colorShade,
        dotBackgroundColor: theme.greys.white,
        dotBorderColor: colorShade,
        className,
      };
    default:
      return {
        backgroundColor: isStandardMonthViewEvent ? 'none' : colorShade,
        borderColor: colorShade,
        textColor: textColorShade,
        dotBackgroundColor: colorShade,
        dotBorderColor: colorShade,
        className,
      };
  }
}

function findColorName(
  calendarVersion: CalendarVersionModel,
  calendarEventVersion: CalendarEventVersionModel,
  colorOverride?: PaletteColorsEnum,
) {
  if (colorOverride) {
    return colorOverride;
  }

  return findCalendarEventVersionColor(calendarEventVersion, calendarVersion);
}

const memoizedMix = memoize(20)((weight: string | number, color: string, otherColor: string) =>
  mix(weight, color, otherColor),
);

export function buildFullCalendarEventGradientBackground(colorShade: string, theme: UserTheme) {
  const lighterColorShade = mix(0.8, colorShade, theme.greys.white);
  return `repeating-linear-gradient(135deg, ${colorShade} 0px, ${colorShade}, 4px, ${lighterColorShade} 4px, ${lighterColorShade} 8px)`;
}

function buildFullCalendarEventTitle(
  localize: Localizer,
  calendarVersionAccessRole: CalendarVersionAccessRolesEnum,
  isPrivateEvent: boolean,
  eventTitle: string | undefined,
  // Can be undefined for non-arbitrary events
  arbitraryEventShowAs: CalendarEventVersionShowAsTypesEnum | undefined,
) {
  if (calendarVersionAccessRole === CalendarVersionAccessRolesEnum.FREE_BUSY) {
    return localize(
      defineMessage({
        id: 'fullcalendar-free-busy-event',
        message: 'Busy',
      }),
    );
  }

  if (isPrivateEvent && !eventTitle && calendarVersionAccessRole === CalendarVersionAccessRolesEnum.READER) {
    return localize(
      defineMessage({
        id: 'fullcalendar-private-event',
        message: 'Private event',
      }),
    );
  }

  if (eventTitle !== undefined) {
    if (eventTitle === '') {
      return localize(
        defineMessage({
          id: 'fullcalendar-untitled-event',
          message: '(Untitled)',
        }),
      );
    }
    return eventTitle;
  }

  if (arbitraryEventShowAs === CalendarEventVersionShowAsTypesEnum.BUSY) {
    return localize(
      defineMessage({
        id: 'fullcalendar-busy-event',
        message: 'Busy',
      }),
    );
  }

  if (arbitraryEventShowAs === CalendarEventVersionShowAsTypesEnum.FREE) {
    return localize(
      defineMessage({
        id: 'fullcalendar-free-event',
        message: 'Free',
      }),
    );
  }

  return localize(
    defineMessage({
      id: 'fullcalendar-untitled-event',
      message: '(Untitled)',
    }),
  );
}

enum FullCalendarViewsEnum {
  WEEK = 'timeGridWeek',
  TIME_GRID = 'timeGrid',
  MONTH = 'dayGridMonth',
}

export function getFullCalendarViewType(viewType: CalendarsViewTypesEnum) {
  switch (viewType) {
    case CalendarsViewTypesEnum.DAY:
    case CalendarsViewTypesEnum.PREVIEW:
      return FullCalendarViewsEnum.TIME_GRID;
    case CalendarsViewTypesEnum.WEEK:
      return FullCalendarViewsEnum.WEEK;
    case CalendarsViewTypesEnum.MONTH:
      return FullCalendarViewsEnum.MONTH;
    default:
      return FullCalendarViewsEnum.WEEK;
  }
}

export function renderCalendarColumnHeader(dateInput: DateInput, timeZone: string) {
  const dateTime = fullcalendarDateInputToLuxonDateTime(dateInput, timeZone);

  return `
    <div>
      <span class="fc-day-header-content-weekday">${dateTime.toFormat('EEE')}</span>
      <br/>
      <span class="fc-day-header-content-day">${dateTime.toFormat('d')}</span>
    </div>
  `;
}

export interface FullCalendarEventDisplayInfo {
  startDate: DateTime;
  endDate: DateTime;
  durationMinutes: number | undefined;
  isAllDay: boolean;
  summary: string | undefined;
  accessRole: CalendarVersionAccessRolesEnum;
  isPrivateEvent: boolean;
  dotBackgroundColor: CleanHtml;
  dotBorderColor: CleanHtml;
  isDurationEditable: boolean;
  // For non-arbitrary events, this will be undefined.
  arbitraryEventShowAs: CalendarEventVersionShowAsTypesEnum | undefined;
}

export function renderFullCalendarEventContent(
  eventDisplayInfo: FullCalendarEventDisplayInfo,
  viewType: CalendarsViewTypesEnum,
  timeFormat: TimeFormatsEnum,
  localize: Localizer,
): CleanHtml {
  const {accessRole, summary, startDate, endDate, isAllDay, arbitraryEventShowAs} = eventDisplayInfo;
  const {durationMinutes, isDurationEditable} = eventDisplayInfo;

  const title = buildFullCalendarEventTitle(
    localize,
    accessRole,
    eventDisplayInfo.isPrivateEvent,
    summary,
    arbitraryEventShowAs,
  );
  const titleHtml = textToHtml(title);

  if (isAllDay) {
    return iSolemnlySwearThisHtmlIsClean(`
      <div class='fc-event-content-wrapper'>
        <div class='fc-single-line-text'>
          ${titleHtml}
        </div>
      </div>
  `);
  }
  // Start and end dates are required for non all-day events.
  if (!startDate || !endDate || !isNumber(durationMinutes)) {
    return iSolemnlySwearThisHtmlIsClean('');
  }

  const shouldUse24hFormat = timeFormat === TimeFormatsEnum.H24;

  const dateTextBase = startDate.toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    ...build12Or24hOptionsForFormat(timeFormat),
  });
  const dateText = shouldUse24hFormat ? dateTextBase : dateTextBase.replace(':00', '');
  const dateHtml = textToHtml(dateText);

  if (viewType === CalendarsViewTypesEnum.MONTH) {
    return iSolemnlySwearThisHtmlIsClean(`
      <div class='fc-event-content-wrapper'>
        <div
          class='fc-month-event-dot'
          style='border-color: ${eventDisplayInfo.dotBorderColor}; background-color: ${eventDisplayInfo.dotBackgroundColor};'
        ></div>
        <span class='fc-date-text-month'>${dateHtml}</span>
        <span class='fc-title-text-month'>${titleHtml}</span>
      </div>
    `);
  }

  // We render events differently depending on their duration:
  // - <35 min: single line text with topic and start time
  // - 35-90 min: one line for topic, one line for start time
  // - >90 min: 3 lines for topic, one line for start time
  if (durationMinutes >= 35) {
    return iSolemnlySwearThisHtmlIsClean(`
      <div class='fc-event-content-wrapper'>
        <div class='${durationMinutes >= 90 ? 'fc-long-title' : 'fc-single-line-text'}'>
          ${titleHtml}
        </div>
        <div class='fc-single-line-text'>
          <span class='fc-date-text'>${dateHtml}</span>
        </div>
      </div>
      ${isDurationEditable ? `<div class='fc-resizer fc-end-resizer'></div>` : ''}
    `);
  }

  if (durationMinutes < 30) {
    // The following logic is to vertically center events with duration >= 15 minutes and < 30 minutes.
    // For each minute beyond 15 minutes (before reaching 30 minutes), the top margin is 0.425px less.
    // This is because for each extra minute of event length, the event height is 0.85px taller.
    const marginTop = String(-6 + (Math.max(durationMinutes, 15) - 15) * (pixelHeightPerMinute / 2));

    return iSolemnlySwearThisHtmlIsClean(`
    <div class='fc-event-content-wrapper'>
      <div class='fc-small-line-text' style="margin-top:${marginTop}px;" >
        ${titleHtml}, <span class='fc-date-text'>${dateHtml}</span>
      </div>
    </div>
    ${isDurationEditable ? `<div class='fc-resizer fc-end-resizer'></div>` : ''}
  `);
  }

  return iSolemnlySwearThisHtmlIsClean(`
    <div class='fc-event-content-wrapper'>
      <div class='fc-single-line-text'>
        ${titleHtml}, <span class='fc-date-text'>${dateHtml}</span>
      </div>
    </div>
    ${isDurationEditable ? `<div class='fc-resizer fc-end-resizer'></div>` : ''}
  `);
}

const checkmarkYesIconContent = require('../../../../libs/shared/assets/src/icon14/checkmarkYes.svg').content;
const xNoIconContent = require('../../../../libs/shared/assets/src/icon14/xNo.svg').content;

export function renderFullCalendarSlotContent(
  start: DateInput,
  end: DateInput,
  timeFormat: TimeFormatsEnum,
  timeZone: string,
): CleanHtml {
  // Start and end dates are required for non all-day events.
  if (!isDateInputJSDate(start) || !isDateInputJSDate(end)) {
    return iSolemnlySwearThisHtmlIsClean('');
  }

  const shouldUse24hFormat = timeFormat === TimeFormatsEnum.H24;
  const startDateTime = fullcalendarDateInputToLuxonDateTime(start, timeZone);
  const startTextBase = startDateTime.toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    ...build12Or24hOptionsForFormat(timeFormat),
  });
  const startText = shouldUse24hFormat ? startTextBase : startTextBase.replace(':00', '');
  const endDateTime = fullcalendarDateInputToLuxonDateTime(end, timeZone);
  const endTextBase = endDateTime.toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    ...build12Or24hOptionsForFormat(timeFormat),
  });
  const endText = shouldUse24hFormat ? endTextBase : endTextBase.replace(':00', '');
  const dateHtml = textToHtml(`${startText} - ${endText}`);

  return iSolemnlySwearThisHtmlIsClean(`
      <div class='fc-event-content-wrapper'>
        <span class='fc-date-text'>${dateHtml}</span>
        <svg class='checkmark-icon' width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
          ${checkmarkYesIconContent}
        </svg>
        <svg class='delete-icon' width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
          ${xNoIconContent}
        </svg>
      </div>
      <div class='fc-resizer fc-end-resizer'></div>
    `);
}

const plusIconContent = require('../../../../libs/shared/assets/src/icon16/plusCircleFilled.svg').content;

export function buildFullCalendarHoverPlaceholder() {
  const placeholderHTML = `
    <div class='fc-hover-placeholder'>
      <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        ${plusIconContent}
      </svg>
    </div>
  `;

  // Convert the above HTML into a DOM element by inserting it in a wrapper div.
  const wrapper = globalDocument.createElement('div');
  wrapper.insertAdjacentHTML('afterbegin', placeholderHTML);
  const placeholder = wrapper.querySelector('.fc-hover-placeholder');

  if (!placeholder) {
    reportToBugsnag(new Error('Unable to create hover placeholder'));
  }

  return placeholder as HTMLDivElement;
}

export function updateFullCalendarHoverPlaceholder(placeholder: HTMLElement, top: number, height: number) {
  // eslint-disable-next-line no-param-reassign
  placeholder.style.top = `${top}px`;
  // eslint-disable-next-line no-param-reassign
  placeholder.style.height = `${height}px`;
}

export function computeHoverPlaceholderHeight(startTimeElement: Element, durationMinutes: number) {
  // Calculate the number of slots the placeholder should span across (each slot is 30 mins).
  let numSlots = Math.ceil(durationMinutes / 30);

  let endTimeElement = startTimeElement;
  while (endTimeElement.nextElementSibling && numSlots > 1) {
    endTimeElement = endTimeElement.nextElementSibling;
    numSlots--;
  }
  const endRect = endTimeElement.getBoundingClientRect();
  const startRect = startTimeElement.getBoundingClientRect();
  return endRect.y + endRect.height - startRect.y;
}

export function getOverlappingEventsWithSchedulingSlot(slotElement: HTMLElement): ReadonlyArray<HTMLElement> {
  const slotRect = slotElement.getBoundingClientRect();
  return _(slotElement.parentElement?.children || [])
    .filter(isHtmlElement)
    .filter((child) => child !== slotElement)
    .filter((element) => {
      const elementRect = element.getBoundingClientRect();
      return (
        slotRect.y + slotRect.height >= elementRect.y && elementRect.y + elementRect.height >= slotRect.y
      );
    })
    .value();
}

interface FullCalendarSegment {
  eventRange: {
    def: {
      extendedProps: {
        calendarEventVersionUid: string;
        calendarVersionId: number;
        instanceStartDate: number | undefined;
        isArbitraryEvent?: boolean;
      };
    };
  };
  el: {
    id: string;
  };
}

export function isFullCalendarSegment(segment: any): segment is FullCalendarSegment {
  return segment && segment.eventRange && segment.eventRange.def && segment.eventRange.def.extendedProps;
}

export function isDateInputJSDate(dateInput: DateInput): dateInput is Date {
  return !(isString(dateInput) || typeof dateInput === 'number' || Array.isArray(dateInput));
}

// Map CalendarWeekDaysEnum to firstDay paramater per https://fullcalendar.io/docs/firstDay
const CalendarWeekDaysEnumToFirstDayMap = {
  [CalendarWeekDaysEnum.SUNDAY]: 0,
  [CalendarWeekDaysEnum.MONDAY]: 1,
  [CalendarWeekDaysEnum.TUESDAY]: 2,
  [CalendarWeekDaysEnum.WEDNESDAY]: 3,
  [CalendarWeekDaysEnum.THURSDAY]: 4,
  [CalendarWeekDaysEnum.FRIDAY]: 5,
  [CalendarWeekDaysEnum.SATURDAY]: 6,
};

export function calendarWeekStartDayToFirstDay(calendarWeekStartDay: CalendarWeekDaysEnum) {
  return CalendarWeekDaysEnumToFirstDayMap[calendarWeekStartDay];
}

export function fullcalendarDateToLuxonDateTime(date: Date, calendar: Calendar): DateTime {
  const {dateEnv} = calendar;

  return fullcalendarDateInputToLuxonDateTime(date, dateEnv.timeZone);
}

export function fullcalendarDateInputToLuxonDateTime(date: DateInput, calendarTimeZone: string): DateTime {
  // If we get a string, we assume it's an ISO string so there's no zone conversion to do.
  if (typeof date === 'string') {
    return DateTime.fromISO(date);
  }

  // If we get a number, it's a timestamp in UTC so we need to convert it to the calendar's timezone.
  if (typeof date === 'number') {
    return DateTime.fromMillis(date, {zone: 'UTC'}).setZone(calendarTimeZone, {keepLocalTime: true});
  }

  // If we get an array, it's a date in the format [year, month, day, hour, minute, second, millisecond] and needs to be converted in the right timezone as well.
  if (isArray(date)) {
    return DateTime.fromObject(
      {
        year: date[0],
        month: date[1] + 1, // convert 0-based to 1-based
        day: date[2],
        hour: date[3],
        minute: date[4],
        second: date[5],
        millisecond: date[6],
      },
      {
        zone: 'UTC',
      },
    ).setZone(calendarTimeZone, {keepLocalTime: true});
  }

  // `date` is a JS Date in UTC when coming from FullCalendar 1.x so we need to parse it as such and correct the timezone.
  // Allegedly modern FullCalendar doesn't need this because the current implementation of the Luxon plugin doesn't need to do that
  // https://github.com/fullcalendar/fullcalendar/blob/main/packages/luxon2/src/convert.ts
  return DateTime.fromJSDate(date, {zone: 'UTC'}).setZone(calendarTimeZone, {keepLocalTime: true});
}
